import React, { useState } from "react";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import {
  User,
  FileCheck,
  PenTool,
  Landmark,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import OnboardingFormShell, {
  OnboardingFormStep,
} from "../../../components/onboarding/OnboardingFormShell";
import SignatureDialog, {
  SignatureType,
} from "../../../components/onboarding/SignatureDialog";
import { useOnboardingForm } from "../../../hooks/useOnboardingForm";

interface SuperChoiceData {
  // Your Details
  legalFirstName: string;
  legalLastName: string;
  dateOfBirth: string;
  mobilePhone: string;
  // Fund Choice
  choiceType: string;
  isSMSF: boolean;
  fundName: string;
  fundAbn: string;
  usi: string;
  memberNumber: string;
  smsfFundName: string;
  smsfAbn: string;
  smsfBankBsb: string;
  smsfBankAccount: string;
  esa: string;
  // Declaration & Signature
  fullName: string;
  signatureDate: string;
  signatureType: string;
  signatureData: string;
  agreed: boolean;
}

const STEPS: OnboardingFormStep[] = [
  { id: 1, label: "Your Details", icon: User },
  { id: 2, label: "Fund Choice", icon: Landmark },
  { id: 3, label: "Declaration & Signature", icon: PenTool },
];

const initialData: SuperChoiceData = {
  legalFirstName: "",
  legalLastName: "",
  dateOfBirth: "",
  mobilePhone: "",
  choiceType: "",
  isSMSF: false,
  fundName: "",
  fundAbn: "",
  usi: "",
  memberNumber: "",
  smsfFundName: "",
  smsfAbn: "",
  smsfBankBsb: "",
  smsfBankAccount: "",
  esa: "",
  fullName: "",
  signatureDate: new Date().toISOString().split("T")[0],
  signatureType: "Draw",
  signatureData: "",
  agreed: false,
};

interface SuperChoiceFormProps {
  /** HR preview mode: renders the hire's submitted form read-only */
  previewRequestId?: string | null;
}

const SuperChoiceForm: React.FC<SuperChoiceFormProps> = ({ previewRequestId }) => {
  const {
    formData,
    updateField,
    currentStep,
    setCurrentStep,
    loading,
    saving,
    isCompleted,
    isEditing,
    submittedAt,
    startEditing,
    saveProgress,
    submit,
    isPreview,
  } = useOnboardingForm<SuperChoiceData>("super-choice", initialData, {
    previewRequestId,
  });

  const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);

  const readOnly = isPreview || (isCompleted && !isEditing);

  const fundFieldsValid =
    !!formData.fundName.trim() &&
    !!formData.fundAbn.trim() &&
    !!formData.usi.trim() &&
    !!formData.memberNumber.trim();

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 1:
        return (
          !!formData.legalFirstName.trim() &&
          !!formData.legalLastName.trim() &&
          !!formData.dateOfBirth &&
          !!formData.mobilePhone.trim()
        );
      case 2:
        if (!formData.choiceType) return false;
        if (formData.choiceType === "default") return fundFieldsValid;
        return formData.isSMSF
          ? !!formData.smsfFundName.trim() &&
              !!formData.smsfAbn.trim() &&
              !!formData.smsfBankBsb.trim() &&
              !!formData.smsfBankAccount.trim() &&
              !!formData.esa.trim()
          : fundFieldsValid;
      case 3:
        return (
          !!formData.fullName.trim() &&
          !!formData.signatureDate &&
          !!formData.signatureData &&
          formData.agreed
        );
      default:
        return true;
    }
  };

  const handleStepChange = (step: number) => {
    if (step > currentStep && !isStepValid(currentStep)) {
      toast.error("Please fill in all required fields before continuing");
      return;
    }
    setCurrentStep(step);
  };

  const canSubmit = isStepValid(1) && isStepValid(2) && isStepValid(3);

  const inputClass = "h-11 rounded-xl border-gray-200 bg-gray-50";
  const labelClass = "text-sm font-medium text-gray-900";

  // ─── STEP 1: Your Details ───
  const renderYourDetails = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Your Details</h3>
        <p className="text-sm text-gray-500">
          Your personal details, as shown on your ATO records. This form
          follows the ATO Standard choice form.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className={labelClass}>
            Legal First Name <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="First name"
            value={formData.legalFirstName}
            onChange={(e) => updateField("legalFirstName", e.target.value)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>
        <div className="space-y-2">
          <label className={labelClass}>
            Legal Last Name <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="Last name"
            value={formData.legalLastName}
            onChange={(e) => updateField("legalLastName", e.target.value)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className={labelClass}>
            Date of Birth <span className="text-red-500">*</span>
          </label>
          <Input
            type="date"
            value={formData.dateOfBirth}
            onChange={(e) => updateField("dateOfBirth", e.target.value)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>
        <div className="space-y-2">
          <label className={labelClass}>
            Mobile Phone <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="e.g. 0412 345 678"
            value={formData.mobilePhone}
            onChange={(e) => updateField("mobilePhone", e.target.value)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>
      </div>
    </div>
  );

  // ─── STEP 2: Fund Choice ───
  // Shared fund detail fields — shown for both the employer default fund and
  // a nominated (non-SMSF) fund.
  const renderFundFields = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-2">
        <label className={labelClass}>
          Fund Name <span className="text-red-500">*</span>
        </label>
        <Input
          placeholder="e.g. AustralianSuper"
          value={formData.fundName}
          onChange={(e) => updateField("fundName", e.target.value)}
          disabled={readOnly}
          className={inputClass}
        />
      </div>
      <div className="space-y-2">
        <label className={labelClass}>
          Fund ABN <span className="text-red-500">*</span>
        </label>
        <Input
          placeholder="e.g. 65 714 394 898"
          value={formData.fundAbn}
          onChange={(e) => updateField("fundAbn", e.target.value)}
          disabled={readOnly}
          className={inputClass}
        />
      </div>
      <div className="space-y-2">
        <label className={labelClass}>
          Unique Superannuation Identifier (USI){" "}
          <span className="text-red-500">*</span>
        </label>
        <Input
          placeholder="e.g. STA0100AU"
          value={formData.usi}
          onChange={(e) => updateField("usi", e.target.value)}
          disabled={readOnly}
          className={inputClass}
        />
      </div>
      <div className="space-y-2">
        <label className={labelClass}>
          Member Number <span className="text-red-500">*</span>
        </label>
        <Input
          placeholder="e.g. 123456789"
          value={formData.memberNumber}
          onChange={(e) => updateField("memberNumber", e.target.value)}
          disabled={readOnly}
          className={inputClass}
        />
      </div>
    </div>
  );

  const renderFundChoice = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Fund Choice</h3>
        <p className="text-sm text-gray-500">
          Choose where your employer's superannuation guarantee contributions
          will be paid.
        </p>
      </div>

      <div className="space-y-2">
        {[
          {
            value: "default",
            label: "Use my employer's default super fund",
          },
          {
            value: "own",
            label: "I want to choose my own super fund",
          },
        ].map((option) => (
          <label
            key={option.value}
            className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
              formData.choiceType === option.value
                ? "bg-blue-50/50 border-blue-200"
                : "bg-white border-gray-200 hover:bg-gray-50"
            } ${readOnly ? "pointer-events-none" : ""}`}
          >
            <input
              type="radio"
              name="choiceType"
              checked={formData.choiceType === option.value}
              onChange={() => updateField("choiceType", option.value)}
              disabled={readOnly}
              className="w-4 h-4"
            />
            <span className="text-sm text-gray-900">{option.label}</span>
          </label>
        ))}
      </div>

      {formData.choiceType === "default" && (
        <div className="space-y-6">
          <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-xl p-4">
            <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              Please{" "}
              <a
                href="https://www.cbussuper.com.au/join-forms/super/your-details"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium underline"
              >
                click here
              </a>{" "}
              (https://www.cbussuper.com.au/join-forms/super/your-details) to
              create your own super fund using the employers default super
              fund, CBUS. This should take no more than 5 minutes. Once you
              create an account and receive your member details, return here
              and enter the information required below.
            </p>
          </div>

          {renderFundFields()}
        </div>
      )}

      {formData.choiceType === "own" && (
        <div className="space-y-6">
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              Your nominated fund must be a complying super fund. You can ask
              your fund for a letter of compliance to give to payroll.
            </p>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isSMSF}
              onChange={(e) => updateField("isSMSF", e.target.checked)}
              disabled={readOnly}
              className="w-4 h-4 rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">
              This is a self-managed super fund (SMSF)
            </span>
          </label>

          {formData.isSMSF ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className={labelClass}>
                  SMSF Name <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder="e.g. Smith Family Super Fund"
                  value={formData.smsfFundName}
                  onChange={(e) => updateField("smsfFundName", e.target.value)}
                  disabled={readOnly}
                  className={inputClass}
                />
              </div>
              <div className="space-y-2">
                <label className={labelClass}>
                  SMSF ABN <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder="e.g. 51 824 753 556"
                  value={formData.smsfAbn}
                  onChange={(e) => updateField("smsfAbn", e.target.value)}
                  disabled={readOnly}
                  className={inputClass}
                />
              </div>
              <div className="space-y-2">
                <label className={labelClass}>
                  Bank Account BSB <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder="e.g. 062-001"
                  value={formData.smsfBankBsb}
                  onChange={(e) => updateField("smsfBankBsb", e.target.value)}
                  disabled={readOnly}
                  className={inputClass}
                />
              </div>
              <div className="space-y-2">
                <label className={labelClass}>
                  Bank Account Number <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder="e.g. 1234 5678"
                  value={formData.smsfBankAccount}
                  onChange={(e) =>
                    updateField("smsfBankAccount", e.target.value)
                  }
                  disabled={readOnly}
                  className={inputClass}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <label className={labelClass}>
                  Electronic Service Address (ESA){" "}
                  <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder="e.g. smsfservices"
                  value={formData.esa}
                  onChange={(e) => updateField("esa", e.target.value)}
                  disabled={readOnly}
                  className={inputClass}
                />
                <p className="text-xs text-gray-500">
                  The ESA alias is provided by your SMSF administrator or
                  messaging provider — it tells payroll where to send
                  SuperStream contribution messages.
                </p>
              </div>
            </div>
          ) : (
            renderFundFields()
          )}
        </div>
      )}
    </div>
  );

  // ─── STEP 3: Declaration & Signature ───
  const renderDeclaration = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Declaration & Signature</h3>
        <p className="text-sm text-gray-500">Read the declaration carefully before signing</p>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <div className="flex items-start gap-2">
          <FileCheck className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700 space-y-2">
            <p className="font-medium text-gray-900">
              I request that all future superannuation guarantee contributions
              from my employer be paid into the fund I have nominated above.
            </p>
            <p className="font-medium text-gray-900">I understand that:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                I can change my choice of fund at any time by completing a new
                form.
              </li>
              <li>
                If I do not make a choice, contributions will be paid into my
                employer's default fund.
              </li>
              <li>The information I have provided is true and correct.</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className={labelClass}>
            Full Legal Name for Signature <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="Your full legal name"
            value={formData.fullName}
            onChange={(e) => updateField("fullName", e.target.value)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>
        <div className="space-y-2">
          <label className={labelClass}>
            Date <span className="text-red-500">*</span>
          </label>
          <Input
            type="date"
            value={formData.signatureDate}
            onChange={(e) => updateField("signatureDate", e.target.value)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>
      </div>

      {/* Digital Signature */}
      <div className="space-y-2">
        <label className={labelClass}>
          Digital Signature <span className="text-red-500">*</span>
        </label>
        {formData.signatureData ? (
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
            <div className="flex items-center gap-3">
              {formData.signatureType === "Draw" ? (
                <img src={formData.signatureData} alt="Signature" className="max-h-10" />
              ) : (
                <span className="font-signature text-2xl">{formData.signatureData}</span>
              )}
            </div>
            {!readOnly && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateField("signatureData", "")}
                className="rounded-lg"
              >
                Clear
              </Button>
            )}
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsSignatureDialogOpen(true)}
            disabled={readOnly}
            className="w-full rounded-xl border-dashed border-gray-300 py-6 gap-2"
          >
            <PenTool className="w-4 h-4" />
            Click to Sign
          </Button>
        )}
      </div>

      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={formData.agreed}
          onChange={(e) => updateField("agreed", e.target.checked)}
          disabled={readOnly}
          className="w-4 h-4 rounded border-gray-300 mt-0.5"
        />
        <span className="text-sm text-gray-700">
          I agree to the declaration above and confirm this electronic signature
          is legally binding.
        </span>
      </label>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderYourDetails();
      case 2:
        return renderFundChoice();
      case 3:
        return renderDeclaration();
      default:
        return renderYourDetails();
    }
  };

  return (
    <>
      <OnboardingFormShell
        title="Superannuation Choice"
        steps={STEPS}
        currentStep={currentStep}
        onStepChange={handleStepChange}
        onSaveProgress={saveProgress}
        onSubmit={submit}
        saving={saving}
        loading={loading}
        submitDisabled={!canSubmit}
        readOnly={readOnly}
        submittedAt={submittedAt}
        onEdit={isPreview ? undefined : startEditing}
        backTo={previewRequestId ? `/my-onboarding?requestId=${previewRequestId}` : undefined}
        isResubmission={isCompleted}
      >
        {renderStepContent()}
      </OnboardingFormShell>

      <SignatureDialog
        open={isSignatureDialogOpen}
        onOpenChange={setIsSignatureDialogOpen}
        onConfirm={(type: SignatureType, data: string) => {
          updateField("signatureType", type);
          updateField("signatureData", data);
        }}
      />
    </>
  );
};

export default SuperChoiceForm;
